import React, { useEffect, useState } from "react";
import {
  resolvePennyDropMismatchState,
  isRawAttemptCapReached,
} from "@/b2b/lib/pennyDropMismatch";
import { useNavigate } from "react-router-dom";
import { bankingStart, fetchData } from "@/lib/helper";
import { useJourneyLender } from "@/lib/journeyLender";
import Div100vh from "react-div-100vh";
import Header from "@/b2b/components/Header";
import TextField from "@/components/common/Input/TextField";
import PrimaryButton from "@/b2b/components/PrimaryButton.jsx";
import ProcessDisbursal from "@/components/banking/ProcessDisbursal"; // Assuming this exists

export default function PennyDrop({ theme }) {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    accountNumber: "",
    ifscCode: "",
  });
  const [data, setData] = useState(null);
  const [lenderKey, setLenderKey] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  // Server said the account holder name does not match the PAN name, with attempts still left.
  // { attemptsLeft } — the tenant can try another account in their own name.
  const [nameMismatch, setNameMismatch] = useState(null);
  const userIdentifier = localStorage.getItem("user_identifier");
  // Reliable partner tag (set by HomeScreen); theme.name resolves async for
  // DB-driven partners and the "housr" fallback would route to the wrong partner.
  const partner = localStorage.getItem("partnerIdentifier");
  const [isLoading, setIsLoading] = useState(false);
  const lender = useJourneyLender(lenderKey);

  useEffect(() => {
    console.log("Fetching data for PennyDrop component");
    const getData = async () => {
      try {
        //setIsLoading(true);
        const resData = await fetchData("journey/full");
        const userData = await fetchData("user/me");
        setLenderKey(resData?.journey?.lender);
        // Bureau-only ("without banking") journeys: the server owns the attempt cap, so the raw
        // count on the row must not lock the form (see isRawAttemptCapReached).
        const creditPath = resData?.journey?.creditPath;
        console.log(resData, "resData");

        const bankingData = resData?.journey?.steps?.find(
          (step) => step.name === "BANKING"
        );
        // console.log("Banking Data:", bankingData);
        const pennyDropData = resData?.journey?.steps?.find(
          (step) => step.name === "PENNY_DROP"
        );
        const bankingRefId = bankingData?.refId;
        // Match this journey's bank record by the BANKING step's refId — the
        // authoritative key the backend (performPennyDrop) uses. `breStatus`
        // is inconsistent in prod ("S" / null / "ACTIVE" / "approved" ...), so
        // a strict `breStatus === "SUCCESS"` match silently misses valid
        // records and the screen renders blank (no account number / IFSC).
        // Fall back to breStatus only if refId can't resolve a record.
        const activeBank =
          resData?.journey?.banking?.find(
            (bank) => bank.refId === bankingRefId
          ) ||
          userData?.banking?.find((bank) => bank.refId === bankingRefId) ||
          resData?.journey?.banking?.find(
            (bank) => bank.breStatus === "SUCCESS"
          ) ||
          userData?.banking?.find((bank) => bank.breStatus === "SUCCESS") ||
          {};
        setData(activeBank);
        console.log("Active Bank Data:", activeBank);
        if (pennyDropData.status === "SUCCESS")
          navigate(
            theme
              ? `/b/${userIdentifier}?partner=${partner}&step=onboarding`
              : "/onboarding"
          );

        // The server appends every attempt to `pennyDropAttempts` as
        // { timestamp, accountNumber, ifscCode, status, response, nameMatch }, where `nameMatch`
        // is { matched, score, reason } on the bureau-only path and null otherwise, and it
        // overwrites `unmaskedAccountNumber` with the account just submitted. So if the LATEST
        // attempt was a name mismatch, the stored account is the WRONG one: auto-submitting it
        // again on every mount/refresh would burn the remaining attempts on the same account and
        // lock the tenant out. Show the form with the inline message instead.
        //
        // "Capped" is the SERVER's call (PENNY_DROP FAILED + NAME_MISMATCH_LIMIT), never a raw
        // count here: after an ops re-open the old mismatches no longer count and the tenant
        // has a fresh budget — see resolvePennyDropMismatchState.
        const attempts = Array.isArray(activeBank.pennyDropAttempts)
          ? activeBank.pennyDropAttempts
          : [];
        const mismatchState = resolvePennyDropMismatchState(
          attempts,
          pennyDropData
        );

        if (mismatchState?.capped) {
          // HomeScreen routes there on resume via pre-flight `sorryReason` too.
          navigate(
            `/b/${userIdentifier}?partner=${partner}&step=penny-drop-sorry`,
            { replace: true }
          );
          return;
        }
        if (mismatchState) {
          setNameMismatch({ attemptsLeft: mismatchState.attemptsLeft });
          setFormData({ accountNumber: "", ifscCode: "" });
        } else if (activeBank.unmaskedAccountNumber && activeBank.ifscCode) {
          setFormData({
            accountNumber: activeBank.unmaskedAccountNumber,
            ifscCode: activeBank.ifscCode,
          });

          if (
            activeBank.pennyDropAttempts &&
            !isRawAttemptCapReached(creditPath, activeBank.pennyDropAttempts)
          ) {
            setNameMismatch(null);
            setIsProcessing(true);
            const response = await bankingStart("banking/penny-drop", {
              accountNumber: activeBank.unmaskedAccountNumber,
              ifscCode: activeBank.ifscCode,
              bankingRefId: activeBank.refId,
            });
            setIsProcessing(false);

            if (response?.account_status === "name_mismatch") {
              setShowErrorMessage(false);
              setNameMismatch({ attemptsLeft: response.attemptsLeft });
              return;
            }
            if (response?.account_status === "name_mismatch_limit") {
              navigate(
                `/b/${userIdentifier}?partner=${partner}&step=penny-drop-sorry`,
                { replace: true }
              );
              return;
            }
            if (response.account_status === "active") {
              navigate(
                theme
                  ? `/b/${userIdentifier}?partner=${
                      partner
                    }&step=onboarding`
                  : "/onboarding"
              );
            }
          } else {
            setShowErrorMessage(true);
          }
        } else if (
          isRawAttemptCapReached(creditPath, activeBank.pennyDropAttempts)
        ) {
          setIsLoading(false);
          setShowErrorMessage(true);
        } else if (activeBank.ifscCode) {
          setIsLoading(false);
          setFormData({
            ifscCode: activeBank.ifscCode,
          });
        }
      } catch (error) {
        console.error(
          "Error fetching journey data or processing penny drop",
          error
        );
        setIsProcessing(false);
        setIsLoading(false);
      } finally {
        setIsProcessing(false);
        setIsLoading(false);
      }
    };
    getData();
  }, [navigate]);

  const handleInputChange = (field, value) => {
    setFormData((prevData) => ({
      ...prevData,
      [field]: value,
    }));
  };

  const handleSubmit = async () => {
    try {
      setNameMismatch(null);
      setIsLoading((prevState) => !prevState);
      const response = await bankingStart("banking/penny-drop", {
        accountNumber: formData?.accountNumber,
        ifscCode: formData?.ifscCode,
        bankingRefId: data.refId,
      });

      if (response?.account_status === "name_mismatch") {
        setShowErrorMessage(false);
        setNameMismatch({ attemptsLeft: response.attemptsLeft });
        return;
      }
      if (response?.account_status === "name_mismatch_limit") {
        navigate(
          `/b/${userIdentifier}?partner=${partner}&step=penny-drop-sorry`,
          { replace: true }
        );
        return;
      }
      if (response.account_status === "active") {
        navigate(
          theme
            ? `/b/${userIdentifier}?partner=${partner}&step=onboarding`
            : "/onboarding"
        );
      } else {
        alert("Account validation failed. Please check your details.");
      }
    } catch (error) {
      console.error("Error submitting account details:", error);
      alert("An error occurred. Please try again.");
    } finally {
      setIsLoading((prevState) => !prevState);
    }
  };

  if (isProcessing) {
    return <ProcessDisbursal />;
  }

  return (
    <Div100vh
      className={`flex flex-col justify-between px-8 py-8 gap-8 ${theme?.backgroundColor} ${theme?.textColor}`}
    >
      <div className="flex flex-col gap-12">
        <Header
          heading="Complete Account Verification"
          subheading="Complete the form below to verify your account"
        />
        <div className="flex flex-col gap-8">
          {nameMismatch && (
            <p className={`text-sm ${theme?.textColor}`}>
              The name on this account does not match your PAN name. Please use
              an account in your own name.
              {nameMismatch.attemptsLeft > 0
                ? ` ${nameMismatch.attemptsLeft} attempt(s) left.`
                : ""}
            </p>
          )}
          <TextField
            name="accountNumber"
            label={"Account Number"}
            value={formData?.accountNumber}
            onChange={(value) => handleInputChange("accountNumber", value)}
            required
            type="number"
            maxLength={16}
            disabled={showErrorMessage}
          />
          {data?.maskedAccountNumber && (
            <p className={`text-xs ${theme?.subHeadingColor}`}>
              Please enter account number ending with{" "}
              {data?.maskedAccountNumber}
            </p>
          )}

          <TextField
            name="ifscCode"
            label="IFSC Code"
            value={formData?.ifscCode ?? ""}
            onChange={(value) =>
              handleInputChange("ifscCode", value?.toUpperCase())
            }
            required
            type="text"
            disabled={showErrorMessage}
          />
        </div>
      </div>
      {showErrorMessage && (
        <p className="text-red-500 text-2xs mt-4">
          Maximum attempts for verification have been reached. Please contact
          our support team for further assistance or call directly at{" "}
          <a
            href="tel:+919354235813"
            className="text-blue-500 underline hover:text-blue-700"
          >
            +919354235813
          </a>
          .
        </p>
      )}

      <div className="flex flex-col items-center justify-center gap-2">
      <PrimaryButton
        text="Submit"
        handleClick={handleSubmit}
        disabled={showErrorMessage}
        loading={isLoading}
        // className={`${theme?.primaryButtonBgColor} ${theme?.primaryButtonTextColor}`}
      />
      {lender && (
        <div className="flex items-center justify-center">
          <span className="text-black text-2xs italic">powered by</span>
          <img src={lender.logoUrl} alt={lender.legalName} className="inline-block max-h-4 ml-1" />
        </div>
      )}
          </div>
    </Div100vh>
  );
}
